import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Space } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import useGlassCard from '../hooks/useGlassCard';
import './Login.css';

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const { login, register, logout } = useAuth();
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  useGlassCard(cardRef);

  // 验证码相关状态
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [loginCaptchaEnabled, setLoginCaptchaEnabled] = useState(false);
  const [captchaData, setCaptchaData] = useState<{ captcha_id: string; captcha_image: string } | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await authAPI.getConfig();
        if (response && response.data && typeof response.data === 'object') {
          setRegistrationEnabled(response.data.registrationEnabled === true);
          // 从 getConfig 获取 captchaEnabled 作为备用
          if (response.data.captchaEnabled !== undefined) {
            setCaptchaEnabled(response.data.captchaEnabled);
          }
        } else {
          setRegistrationEnabled(false);
        }
      } catch (error) {
        console.error('获取系统配置失败:', error);
        setRegistrationEnabled(false);
      }
    };
    fetchConfig();
  }, []);

  // 获取安全设置（验证码是否开启）
  useEffect(() => {
    const fetchSecuritySettings = async () => {
      try {
        const response = await authAPI.getSecuritySettings();
        const data = response.data?.data || response.data;
        if (data && data.captcha_enabled !== undefined) {
          setCaptchaEnabled(data.captcha_enabled);
        }
        if (data && data.login_captcha_enabled !== undefined) {
          setLoginCaptchaEnabled(data.login_captcha_enabled);
        }
      } catch (error) {
        console.error('获取安全设置失败:', error);
      }
    };
    fetchSecuritySettings();
  }, []);

  // 获取验证码
  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const response = await authAPI.getCaptcha();
      const data = response.data?.data || response.data;
      if (data && (data.captcha_id || data.captchaId)) {
        setCaptchaData({
          captcha_id: data.captcha_id || data.captchaId,
          captcha_image: data.captcha_image || data.captchaImage || '',
        });
      }
    } catch (error) {
      console.error('获取验证码失败:', error);
    } finally {
      setCaptchaLoading(false);
    }
  };

  // 当切换到注册标签且验证码开启时，自动获取验证码
  useEffect(() => {
    if (activeTab === 'register' && captchaEnabled) {
      fetchCaptcha();
    }
  }, [activeTab, captchaEnabled]);

  // 当切换到登录标签且登录验证码开启时，自动获取验证码
  useEffect(() => {
    if (activeTab === 'login' && loginCaptchaEnabled) {
      fetchCaptcha();
    }
  }, [activeTab, loginCaptchaEnabled]);

  const onLogin = async (values: { username: string; password: string; captcha_answer?: string }) => {
    setLoading(true);
    try {
      const fingerprint = getDeviceFingerprint();
      const loginData: any = {
        username: values.username,
        password: values.password,
        device_fingerprint: fingerprint,
      };
      if (loginCaptchaEnabled && captchaData) {
        loginData.captcha_id = captchaData.captcha_id;
        loginData.captcha_answer = values.captcha_answer;
      }
      await login(values.username, values.password, fingerprint, loginData.captcha_id, loginData.captcha_answer);
      message.success('登录成功！');
      navigate('/home');
    } catch (error: any) {
      if (loginCaptchaEnabled) fetchCaptcha();
      if (error.accountDisabled && !error.isExpired) {
        message.error('账号已被禁用，请联系管理员');
        return;
      }

      if (error.isExpired) {
        navigate('/home');
        return;
      }

      message.error(error.message || '用户名或密码错误');
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (values: {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
    invite_code: string;
    captcha_answer?: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    // 验证码校验
    if (captchaEnabled && !values.captcha_answer) {
      message.error('请输入验证码');
      return;
    }

    setLoading(true);
    try {
      const fingerprint = getDeviceFingerprint();
      const captchaInfo = captchaEnabled && captchaData && values.captcha_answer
        ? { captcha_id: captchaData.captcha_id, captcha_answer: values.captcha_answer }
        : undefined;
      await register(values.username, values.email, values.password, values.invite_code, fingerprint, captchaInfo);
      message.success('注册成功！');
      navigate('/home');
    } catch (error: any) {
      message.error(error.message || '注册失败，请重试');
      // 注册失败时刷新验证码
      if (captchaEnabled) {
        fetchCaptcha();
        registerForm.setFieldsValue({ captcha_answer: '' });
      }
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (tab: 'login' | 'register') => {
    setActiveTab(tab);
  };

  return (
    <div className="login-page">
      <div
        ref={cardRef}
        className="login-card"
      >
        <div className="login-card-header">
          <div className="login-card-logo">🔐</div>
          <h1 className="login-card-title">Tomato 综合站</h1>
          <p className="login-card-subtitle">安全 · 高效 · 极简</p>
        </div>

        <div className="login-tabs">
          <button
            className={`login-tab ${activeTab === 'login' ? 'login-tab-active' : ''}`}
            onClick={() => switchTab('login')}
          >
            登录
          </button>
          {registrationEnabled && (
            <button
              className={`login-tab ${activeTab === 'register' ? 'login-tab-active' : ''}`}
              onClick={() => switchTab('register')}
            >
              注册
            </button>
          )}
        </div>

        <div className="login-form-wrapper">
          {activeTab === 'login' && (
            <div className="login-form-panel" key="login">
              <Form
                form={loginForm}
                name="login"
                onFinish={onLogin}
                autoComplete="off"
                className="login-form"
              >
                <Form.Item
                  name="username"
                  rules={[
                    { required: true, message: '请输入用户名' },
                    { min: 3, message: '用户名至少3个字符' }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="用户名"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: '请输入密码' },
                    { min: 6, message: '密码至少6个字符' }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                  />
                </Form.Item>

                {loginCaptchaEnabled && captchaData && (
                  <Form.Item
                    name="captcha_answer"
                    rules={[{ required: true, message: '请输入验证码' }]}
                  >
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Input placeholder="验证码" style={{ flex: 1 }} />
                      <img
                        src={captchaData.captcha_image}
                        alt="验证码"
                        style={{ height: 32, cursor: 'pointer', borderRadius: 4 }}
                        onClick={fetchCaptcha}
                        title="点击刷新"
                      />
                      <Button icon={<ReloadOutlined />} onClick={fetchCaptcha} loading={captchaLoading} />
                    </div>
                  </Form.Item>
                )}

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="login-btn"
                  >
                    登录
                  </Button>
                </Form.Item>
              </Form>

              <div className="login-switch-hint">
                {registrationEnabled && (
                  <>
                    还没有账号？<span className="login-switch-link" role="button" tabIndex={0} onClick={() => switchTab('register')}>立即注册</span>
                    <span className="login-switch-divider">|</span>
                  </>
                )}
                <span className="login-switch-link" role="button" tabIndex={0} onClick={() => navigate('/ios-op-login')}>iOS OP登录</span>
              </div>
            </div>
          )}

          {activeTab === 'register' && registrationEnabled && (
            <div className="login-form-panel" key="register">
              <Form
                form={registerForm}
                name="register"
                onFinish={onRegister}
                autoComplete="off"
                className="login-form"
              >
                <Form.Item
                  name="username"
                  rules={[
                    { required: true, message: '请输入用户名' },
                    { min: 3, message: '用户名至少3个字符' }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="用户名"
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: '请输入邮箱' },
                    { type: 'email', message: '请输入有效的邮箱地址' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="邮箱"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: '请输入密码' },
                    { min: 6, message: '密码至少6个字符' }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  rules={[{ required: true, message: '请确认密码' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="确认密码"
                  />
                </Form.Item>

                <Form.Item
                  name="invite_code"
                  rules={[
                    { required: false },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="邀请码（选填）"
                    maxLength={12}
                  />
                </Form.Item>

                {captchaEnabled && (
                  <Form.Item
                    name="captcha_answer"
                    rules={[{ required: true, message: '请输入验证码' }]}
                  >
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        placeholder="请输入验证码"
                        style={{ flex: 1, height: 40 }}
                      />
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 40,
                        background: '#f5f5f5',
                        borderRadius: '0 6px 6px 0',
                        overflow: 'hidden',
                        border: '1px solid #d9d9d9',
                        borderLeft: 'none',
                      }}>
                        {captchaData?.captcha_image ? (
                          <img
                            src={captchaData.captcha_image}
                            alt="验证码"
                            style={{ height: 40, display: 'block' }}
                          />
                        ) : (
                          <span style={{ padding: '0 12px', fontSize: 14, color: '#999' }}>加载中...</span>
                        )}
                        <ReloadOutlined
                          spin={captchaLoading}
                          onClick={fetchCaptcha}
                          style={{ cursor: 'pointer', color: '#1677ff', fontSize: 14, padding: '0 8px' }}
                        />
                      </div>
                    </Space.Compact>
                  </Form.Item>
                )}

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="login-btn"
                  >
                    注册
                  </Button>
                </Form.Item>
              </Form>

              <div className="login-switch-hint">
                已有账号？<span className="login-switch-link" role="button" tabIndex={0} onClick={() => switchTab('login')}>立即登录</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
